// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { useUniverse } from '../contexts/UniverseContext';
import { useAuth } from '../contexts/AuthContext';
import ChangePassword from './ChangePassword';
import RankDisplay from './RankDisplay';
import RankDetailsModal from './RankDetailsModal';
import * as StatusColorUtils from '../utils/ui/StatusColorUtils';
import './HUD.css';
import Tooltip from './Tooltip';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const HUD = ({ systemPower, warpSpeed, onOpenSaveLoad, onOpenReset, onOpenLogin }) => {
  const { getCurrentUniverseInfo } = useUniverse();
  const { user, logout } = useAuth();
  const universeInfo = getCurrentUniverseInfo();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showRankDetails, setShowRankDetails] = useState(false);
  const [shipName, setShipName] = useState('USS Enterprise');
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [coordinates, setCoordinates] = useState({
    x: 2847.3,
    y: 1923.7,
    z: 4821.2
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      // Simulate coordinate changes during warp
      if (warpSpeed > 0) {
        setCoordinates(prev => ({
          x: prev.x + (Math.random() - 0.5) * warpSpeed * 10,
          y: prev.y + (Math.random() - 0.5) * warpSpeed * 10,
          z: prev.z + (Math.random() - 0.5) * warpSpeed * 10
        }));
      }
    }, 100);

    return () => clearInterval(timer);
  }, [warpSpeed]);

  // Load ship name from localStorage
  useEffect(() => {
    const loadShipName = () => {
      const saved = localStorage.getItem(STORAGE_KEYS.SHIP_NAME);
      if (saved) setShipName(saved);
    };
    
    loadShipName();
    
    // Listen for ship name changes
    window.addEventListener('storage', loadShipName);
    return () => window.removeEventListener('storage', loadShipName);
  }, []);

  const formatCoordinate = (coord) => coord.toFixed(1);

  // Calculate stardate (3000 years in the future)
  const getStardate = () => {
    const futureDate = new Date(currentTime);
    futureDate.setFullYear(futureDate.getFullYear() + 3000);
    return futureDate.toLocaleDateString() + ' ' + futureDate.toLocaleTimeString();
  };
  
  // Get power status with color coding
  const powerStatus = StatusColorUtils.formatStatusPercentage(systemPower, 100, true);
  const powerLevel = StatusColorUtils.getStatusLevel(systemPower, 100);

  return (
    <div className="hud-simple">
      {/* Top HUD Bar Only */}
      <div className="hud-top">
        <div className="hud-section reset-indicator">
          <div className="hud-label">RESET</div>
          <div className="hud-value glow">
            <span className="reset-key">F3</span>
          </div>
        </div>

        <div className="hud-section hud-universe">
          <div className="hud-label">UNIVERSE</div>
          <div className="hud-value glow" style={{ color: universeInfo.theme.primary }}>
            {universeInfo.codename}
          </div>
        </div>

        <div className="hud-section hud-vessel">
          <div className="hud-label">VESSEL</div>
          <Tooltip text="Click SHIP button in console to rename" position="bottom">
            <div className="hud-value glow" style={{ color: '#00ffff' }}>
              {shipName}
            </div>
          </Tooltip>
        </div>

        <div className="hud-section hud-stardate">
          <div className="hud-label">STARDATE</div>
          <Tooltip text="Current game time in stardate format" position="bottom">
            <div className="hud-value glow">
              {getStardate()}
            </div>
          </Tooltip>
        </div>

        <div className="hud-section center hud-vessel-status">
          <div className="hud-label">VESSEL STATUS</div>
          <Tooltip text="Ship power level - affects all systems" position="bottom">
            <div className="hud-value">
              <span 
                className={`status-indicator ${powerLevel} ${StatusColorUtils.getPulseClass(systemPower, 100)}`}
                style={{ color: powerStatus.color }}
              >
                {powerStatus.icon} {StatusColorUtils.getStatusText(systemPower, 100)}
              </span>
            </div>
          </Tooltip>
        </div>

        <div className="hud-section hud-warp">
          <div className="hud-label">WARP FACTOR</div>
          <div className="hud-value glow">
            {warpSpeed.toFixed(1)}
          </div>
        </div>

        <div className="hud-section hud-power">
          <div className="hud-label">POWER CORE</div>
          <div className="hud-value">
            <span 
              className={`status-indicator ${powerLevel} ${StatusColorUtils.getPulseClass(systemPower, 100)}`}
              style={{ 
                color: powerStatus.color,
                textShadow: `0 0 10px ${powerStatus.color}`
              }}
            >
              {powerStatus.icon} {powerStatus.text}
            </span>
          </div>
        </div>

        <div className="hud-section hud-coords">
          <div className="hud-label">COORDINATES</div>
          <Tooltip text="Current position in space (X, Y, Z)" position="bottom">
            <div className="hud-value">
              {formatCoordinate(coordinates.x)}, {formatCoordinate(coordinates.y)}, {formatCoordinate(coordinates.z)}
            </div>
          </Tooltip>
        </div>
        
        {/* Rank Display */}
        <div className="hud-section hud-rank">
          <RankDisplay 
            stats={{
              systemsVisited: parseInt(localStorage.getItem(STORAGE_KEYS.SYSTEMS_VISITED) || '0'),
              combatWins: parseInt(localStorage.getItem(STORAGE_KEYS.COMBAT_WINS) || '0'),
              creditsEarned: parseInt(localStorage.getItem(STORAGE_KEYS.TOTAL_CREDITS_EARNED) || '0')
            }}
            onClick={() => setShowRankDetails(true)}
          />
        </div>
        
        <div className="hud-section help-indicator">
          <div className="hud-label">HELP</div>
          <div className="hud-value glow">
            <span className="help-key">F1</span>
          </div>
        </div>

        {user ? (
          <div className="hud-section hud-user">
            <div className="hud-label">USER</div>
            <div className="hud-value hud-user-actions">
              <span className="hud-username">{user.username}</span>
              <button
                className="hud-action-btn hud-action-secondary"
                onClick={() => setShowChangePassword(true)}
              >
                Change PW
              </button>
              <button
                className="hud-action-btn hud-action-danger"
                onClick={() => {
                  logout();
                }}
              >
                Logout
              </button>
              <button
                className="hud-action-btn hud-action-icon"
                onClick={onOpenSaveLoad}
                title="Save/Load Game"
              >
                💾
              </button>
              <button
                className="hud-action-btn hud-action-icon hud-action-warn"
                onClick={onOpenReset}
                title="Reset Game (F3)"
              >
                🔄
              </button>
            </div>
          </div>
        ) : (
          <div className="hud-section hud-user">
            <div className="hud-label">SYNC SAVES</div>
            <div className="hud-value hud-user-actions">
              <button
                className="hud-action-btn hud-action-primary"
                onClick={onOpenLogin}
              >
                Sign In
              </button>
              <button
                className="hud-action-btn hud-action-icon"
                onClick={onOpenSaveLoad}
                title="Save/Load Game"
              >
                💾
              </button>
              <button
                className="hud-action-btn hud-action-icon hud-action-warn"
                onClick={onOpenReset}
                title="Reset Game (F3)"
              >
                🔄
              </button>
            </div>
          </div>
        )}
      </div>

      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}

      {showRankDetails && (
        <RankDetailsModal
          isOpen={true}
          stats={{
            systemsVisited: parseInt(localStorage.getItem(STORAGE_KEYS.SYSTEMS_VISITED) || '0'),
            combatWins: parseInt(localStorage.getItem(STORAGE_KEYS.COMBAT_WINS) || '0'),
            creditsEarned: parseInt(localStorage.getItem(STORAGE_KEYS.TOTAL_CREDITS_EARNED) || '0')
          }}
          onClose={() => setShowRankDetails(false)}
        />
      )}

      {/* Crosshair */}
      <div className="crosshair">
        <div className="crosshair-h"></div>
        <div className="crosshair-v"></div>
        <div className="crosshair-center"></div>
      </div>
    </div>
  );
};

export default HUD;